import { useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { Eye, Sparkles } from "lucide-react";

import { fetchParticipantById } from "@/lib/api/participants";
import { getParticipantSession } from "@/lib/participant-session";

import { Button } from "@/components/ui/Button";
import { MobileFrame } from "@/components/ui/MobileFrame";

/**
 * V2 spec step 6 — "One more thing, record a pitch".
 *
 * Sits between GoalsPage and FeedPage. Two benefit cards explain
 * what recording earns the participant, then a Record / Maybe later
 * choice. Picking Maybe later drops them straight into the feed as
 * a text-only profile; the FeedPage already nudges them to record.
 *
 * Auto-skipped for participants who already have a video — typical
 * for returning users who hit this URL on accident, or who edited
 * goals after recording.
 */
export function PitchAdvicePage() {
  const { slug } = useParams<{ slug: string }>();
  const navigate = useNavigate();
  const session = slug ? getParticipantSession(slug) : null;
  const participantId = session?.participantId ?? "";

  const { data: participant } = useQuery({
    queryKey: ["participant-pitch-advice", participantId],
    queryFn: () => fetchParticipantById(participantId),
    enabled: Boolean(participantId),
  });

  useEffect(() => {
    if (!slug) return;
    if (!session) navigate(`/e/${slug}`, { replace: true });
  }, [slug, session, navigate]);

  // Already has a video → skip the upsell.
  useEffect(() => {
    if (!slug || !participant) return;
    if (participant.video_storage_path) {
      navigate(`/e/${slug}/feed`, { replace: true });
    }
  }, [slug, participant, navigate]);

  if (!session) return null;

  return (
    <MobileFrame>
      <div className="flex flex-1 flex-col px-5 py-7">
        <h1 className="text-[22px] font-bold leading-[1.2] tracking-[-0.01em] text-[#0a0a0a]">
          One more thing — record a pitch
        </h1>
        <p className="mt-2 text-[13.5px] leading-relaxed text-[#737373]">
          A 60-second video does two things for you:
        </p>

        <Benefit
          icon={<Eye className="h-4.5 w-4.5" />}
          title="Appear in others' feeds"
          body="People can find you without you liking them first."
        />
        <Benefit
          icon={<Sparkles className="h-4.5 w-4.5" />}
          title="See how you come across"
          body="A read on your positioning against your goals."
        />

        <div className="mt-7 flex flex-col gap-2.5">
          <Button
            type="button"
            fullWidth
            onClick={() => navigate(`/e/${slug}/pitch`)}
          >
            Record pitch
          </Button>
          <Button
            type="button"
            variant="secondary"
            fullWidth
            onClick={() => navigate(`/e/${slug}/feed`)}
          >
            Maybe later
          </Button>
        </div>

        <p className="mt-3 text-center text-[12px] leading-relaxed text-[#737373]">
          You can add a pitch anytime from the feed.
        </p>
      </div>
    </MobileFrame>
  );
}

function Benefit({
  icon,
  title,
  body,
}: {
  icon: React.ReactNode;
  title: string;
  body: string;
}) {
  return (
    <div className="mt-3 flex items-start gap-3 rounded-[14px] border border-[#e5e5e5] bg-white p-3.5">
      <div className="grid h-9 w-9 shrink-0 place-items-center rounded-full bg-[#eff3fe] text-[#2563eb]">
        {icon}
      </div>
      <div className="min-w-0 flex-1">
        <p className="text-[14px] font-semibold text-[#0a0a0a]">{title}</p>
        <p className="mt-1 text-[12.5px] leading-snug text-[#737373]">{body}</p>
      </div>
    </div>
  );
}
